import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, MaxLengthValidator, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { version } from 'package.json';
// tslint:disable-next-line:ordered-imports
import { Subject, from, throwError, BehaviorSubject, throwError as _throw } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  AppConfig,
  BackendResponse,
  DataManagementService,
  DBService,
  DeviceService,
  DialogService,
  HttpService,
  Logger,
  LoggingService,
  LoginService,
  ScreenSize,
  ToastrService,
  TranslateService,
} from 'vas-mobile-api';

import { Captcha } from '../models/captcha';
import { PasswordResetComponent } from '../password-reset/password-reset.component';
import { DataExchangeService } from '../services/dataexchange.service';

// ------ Models -------------------------
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {

  public logger: Logger;
  public supportedLanguages: string[];
  public languages;

  private ngUnsubscribe: Subject<void> = new Subject();
  public showMobile: boolean;

  public loginForm: FormGroup;
  public authCodeForm: FormGroup;

  public captchaImg: string;
  private captchaId: number;
  public myVersion = '';

  private showCodeField = new BehaviorSubject<boolean>(false);
  public showCodeField$ = this.showCodeField.asObservable();

  constructor(
    private loggingService: LoggingService,
    private translateVasService: TranslateService,
    private loginService: LoginService,
    private router: Router,
    private dataexchangeService: DataExchangeService,
    private dataMgmtService: DataManagementService,
    private deviceService: DeviceService,
    private dbService: DBService,
    private appConfig: AppConfig,
    private httpService: HttpService,
    private toastrService: ToastrService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private des: DataExchangeService
  ) { }

  ngOnInit() {
    this.languages = {};

    this.myVersion = 'v' + version;
    this.logger = this.loggingService.getLogger(LoginComponent.name);

    const currLang = this.translateVasService.getLanguage();
    // this.supportedLanguages = this.translateVasService.getSupportedLanguages();

    this.authCodeForm = new FormGroup({
      authCode: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]{6}$')
      ])
    });

    this.loginForm = new FormGroup({
      user: new FormControl(null, Validators.required),
      password: new FormControl(null, Validators.required),
      language: new FormControl(currLang),
      plantCode: new FormControl(),
      companyCode: new FormControl(),
      captcha: new FormControl(null, Validators.required)
    });
    Object.preventExtensions(this.loginForm);
    Object.seal(this.loginForm);
    this.setViewMode(currLang);

    this.loginForm.get('language').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => {
      this.translateVasService.setLanguage(value);
      this.setViewMode(value);
    });

    this.dataMgmtService.getCurrentServer().then((data) => {
      if (data) {
        if (data.plantCodes.length > 0) {
          this.loginForm.get('plantCode').patchValue(data.plantCodes[0].key);
        }
        if (data.companyCodes.length > 0) {
          this.loginForm.get('companyCode').patchValue(data.companyCodes[0].key);
        }
      }
    });

    this.requestLanguages();
    this.requestCaptcha();

    this.deviceService.size.pipe(takeUntil(this.ngUnsubscribe)).subscribe((size) => {
      this.showMobile = size <= ScreenSize.sm;
    });

    this.dataexchangeService.changeTitelBarName('Login');

  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public requestCaptcha() {
    this.logger.debug('Requesting Captcha');
    this.des.showLoadingIndicator(true);
    this.httpService.get<BackendResponse<Captcha>>({ pathId: 'generateCaptcha', withToken: false }).subscribe((response) => {
      this.captchaId = response.data.captchaId;
      this.captchaImg = response.data.captchaImg;
    }, (error) => {
      this.des.showLoadingIndicator(false);
    }, () => {
      this.des.showLoadingIndicator(false);
    });

  }

  public omit_special_char(event) {
    let c;
    c = event.charCode;  // k = event.keyCode;  (Both can be used)
    return ((c > 64 && c < 91) || (c > 96 && c < 123) || c === 8 || c === 32 || (c >= 48 && c <= 57));
  }

  public requestLanguages() {
    this.logger.info('Sent request for languages ');

    this.supportedLanguages = this.translateVasService.getSupportedLanguages();

    if (this.supportedLanguages.length > 0 && this.supportedLanguages.indexOf(this.loginForm.get('language').value) < 0) {
      this.loginForm.get('language').patchValue(this.supportedLanguages[0]);
    }

    this.httpService.getKeycodesGetAllKeysAndStrings('SPRACHEN', true, true, true).subscribe((resultData) => {
      const sprachen = resultData.data.SPRACHEN;
      sprachen['AR'] = '(Arabic) عربي';
      this.dbService.setItem('sprachen', sprachen);
      this.languages = resultData.data.SPRACHEN || {};
      Object.freeze(this.languages);
    });

  }

  newSubmitPreLogin() {
    this.des.showLoadingIndicator(true);
    const login$ = this.httpService.get<BackendResponse<boolean>>({
      pathId: 'validateCaptcha', withToken: false,
      urlParams: { captchaId: this.captchaId, answer: this.loginForm.value.captcha }
    }).pipe(
      tap((result) => console.log('captcha result ', result)),
      tap(() => this.logger.info('Try login with', this.loginForm.value.user)),
      catchError((e) => {
        this.showCodeField.next(false);
        this.logger.info('Captcha Error', e);
        this.showErrorDialogCaptcha();
        return throwError(e);
      }),
      switchMap(() => from(this.loginService.prelogin(
        this.loginForm.value.user, this.loginForm.value.password, this.loginForm.value.companyCode, this.loginForm.value.plantCode))),
      tap((result) => this.logger.info('Login succesful', result)),
      tap((result) => {
        if (result.data.userType === 'SUPP') {
          this.showCodeField.next(true);
        } else {
          this.newOnSubmit();
        }

      }),
      catchError((e) => {
        this.showCodeField.next(false);
        this.logger.info('Cred Error', e);
        this.showErrorDialogCred();
        return throwError(e);
      }),
      finalize(() => this.des.showLoadingIndicator(false)),

    ).subscribe();
  }

  showErrorDialogCaptcha() {
    this.showCodeField.next(false);
    this.dialogService.openConfirmDialog(
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_ERROR_CAPTCHA'),
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_WRONG_CAPTCHA'),
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
    ).subscribe(() => {
      this.requestCaptcha();
      this.toastrService.error('Incorrect Captcha answer');
      this.loginForm.get('captcha').reset();
    });
  }

  showErrorDialogCred() {
    this.showCodeField.next(false);
    this.dialogService.openConfirmDialog(
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_WRONG_CRED'),
      this.translateVasService.translateFirstLetterUpperCase('MESSAGE:WRONG_CRED'),
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
    ).subscribe(() => {
      this.requestCaptcha();
      this.loginForm.get('password').reset();
      this.loginForm.get('captcha').reset();
      this.showCodeField.next(false);
      this.logger.debug('User confirm that he has entered wrong credential');
    });
  }

  showErrorDialogCodeExpired() {
    this.showCodeField.next(false);
    this.dialogService.openConfirmDialog(
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_EXPIRED_CODE'),
      this.translateVasService.translateFirstLetterUpperCase('MESSAGE:EXPIRED_CODE'),
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
    ).subscribe(() => {
      this.requestCaptcha();
      this.loginForm.get('password').reset();
      this.loginForm.get('captcha').reset();
      this.loginForm.reset();

      this.authCodeForm.get('authCode').reset();
      this.authCodeForm.reset();
      this.showCodeField.next(false);

      this.logger.debug('User confirm that he has entered wrong credential');
    });
  }

  showErrorDialogCodeInvalid() {
    this.dialogService.openConfirmDialog(
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_INVALID_CODE'),
      this.translateVasService.translateFirstLetterUpperCase('MESSAGE:WRONG_CODE'),
      this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
    ).subscribe(() => {
      this.authCodeForm.get('authCode').reset();
      this.showCodeField.next(true);
      this.authCodeForm.reset();
      this.logger.debug('User confirm that he has entered wrong credential');
    });
  }

  newOnSubmit() {
    this.des.showLoadingIndicator(true);
    this.logger.info('Try login with', this.loginForm.value.user);
    const login$ = from(this.loginService.login(
      this.loginForm.value.user,
      this.loginForm.value.password,
      this.loginForm.value.companyCode,
      this.loginForm.value.plantCode,
      this.authCodeForm.value.authCode)).pipe(tap((result) => {
        this.logger.debug('Object of login', result);
      }),
        catchError((e) => {
          if (e.status === 401) {
            this.toastrService.error(this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_INVALID_CODE'));
            this.showErrorDialogCodeInvalid();
          } else if (e.status === 403) {
            this.toastrService.error(this.translateVasService.translateFirstLetterUpperCase('MESSAGE:EXPIRED_CODE'));
            this.showErrorDialogCodeExpired();
          } else {
            this.toastrService.error(this.translateVasService.translateFirstLetterUpperCase('MESSAGE:EXPIRED_CODE'));
            this.showErrorDialogCodeExpired();
          }
          return throwError(e);
        }),
        switchMap(() => from(this.httpService.get<BackendResponse<boolean>>({ pathId: 'getVIPFlag' }))),
        tap((result) => {
          this.logger.debug('Is user isVIP', result);
          if (result) {
            this.dbService.setItem('isVIP', result.data);
          } else {
            this.dbService.setItem('isVIP', false);
          }
        }),
        catchError((e) => {
          this.dbService.setItem('isVIP', false);
          return throwError(e);
        }),
        finalize(() => {
          this.des.showLoadingIndicator(false);
          this.router.navigate(['dashboard']);
        })
      ).subscribe();
  }

  onSubmit() {
    const formModel = this.loginForm.value;
    this.des.showLoadingIndicator(true);
    this.httpService.get<BackendResponse<boolean>>({
      pathId: 'validateCaptcha', withToken: false,
      urlParams: { captchaId: this.captchaId, answer: formModel.captcha }
    }).subscribe((response) => {
      if (response.data) {
        this.logger.info('Try login with', formModel.user);

        this.loginService.login(formModel.user, formModel.password, formModel.companyCode, formModel.plantCode, this.authCodeForm.value.authCode).then(() => {
          this.logger.info('login successful');
          this.httpService.get<BackendResponse<boolean>>({ pathId: 'getVIPFlag' }).subscribe((result) => {
            this.dbService.setItem('isVIP', result.data);
            this.router.navigate(['dashboard']);
          }, () => {
            this.dbService.setItem('isVIP', false);
            this.router.navigate(['dashboard']);
          });
          this.des.showLoadingIndicator(false);
        }, (e) => {
          this.des.showLoadingIndicator(false);
          this.dialogService.openConfirmDialog(
            this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_WRONG_CRED'),
            this.translateVasService.translateFirstLetterUpperCase('MESSAGE:WRONG_CRED'),
            this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
          ).subscribe(() => {
            this.requestCaptcha();
            this.loginForm.get('password').reset();
            this.loginForm.get('captcha').reset();
            this.logger.debug('User confirm that he has entered wrong credential');
          });
          this.logger.error('login failed', e);
        });
      } else {
        this.des.showLoadingIndicator(false);
        this.dialogService.openConfirmDialog(
          this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_ERROR_CAPTCHA'),
          this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_WRONG_CAPTCHA'),
          this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
        ).subscribe(() => {
          this.requestCaptcha();
          this.toastrService.error('Incorrect Captcha answer');
          this.loginForm.get('captcha').reset();
        });
      }
    }, (e) => {
      this.des.showLoadingIndicator(false);
      this.dialogService.openConfirmDialog(
        this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_ERROR_CAPTCHA'),
        this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_WRONG_CAPTCHA'),
        this.translateVasService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM')
      ).subscribe(() => {
        this.des.showLoadingIndicator(false);
        this.requestCaptcha();
        this.toastrService.error('Incorrect Captcha answer');
        this.loginForm.get('captcha').reset();
      });
    }, () => {
      this.des.showLoadingIndicator(false);
    });
  }

  private setViewMode(language: string) {
    if (language === 'AR') {
      this.dataexchangeService.changeViewMode('rtl');
    } else {
      this.dataexchangeService.changeViewMode('ltr');
    }
  }

  public showResetPasswordPopup() {
    this.dialog.open(PasswordResetComponent, {
      width: '300px',
      height: '300px'
    });
  }

}
